import React, { useState, useEffect } from "react";

import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { Form, Pagination, ButtonGroup, Button } from "react-bootstrap";
import { Stack } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { Toast, ToastContainer } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { remoteConfig } from "services/firebase";
import GameService from "services/GameService";
import RoundForm from "./RoundForm";
import AddQuestionForm from "./AddQuestionForm";
import RoundTab from "./RoundTab";
import AddRoundDropdown from "./AddRoundDropdown";
import InfoRoundForm from "./InfoRoundForm";

// Add Toast imports

const DEFAULT_MATCH_VALUE = 1000;
const DEFAULT_MODE = "points";
const DEFAULT_SCHEME = "turbo";
const DEFAULT_CATEGORIES = [];
const DEFAULT_SHUFFLE = true;

function NewGame({ game, onChange }) {
  const [name, setName] = useState(game?.name ?? "");
  const [questions, setQuestions] = useState(game?.questions ?? {});
  const [page, setPage] = useState(0);
  const [hasMigrated, setHasMigrated] = useState(false);

  const [surveyUrl, setSurveyUrl] = useState(game?.surveyUrl ?? "");

  
  // Game configurations per individual round
  const [configs, setConfigs] = useState(() => {
    const existingRounds = game?.rounds || [];
    
    // If no existing rounds, create a default game round
    if (existingRounds.length === 0) {
      return [{
        id: 0,
        type: 'game',
        matchValue: DEFAULT_MATCH_VALUE,
        models: DEFAULT_CATEGORIES,
        mode: DEFAULT_MODE,
        scheme: DEFAULT_SCHEME,
        shuffle: DEFAULT_SHUFFLE,
      }];
    }
    
    // Ensure backward compatibility by adding type field to existing configs
    return existingRounds.map((round, index) => ({
      ...round,
      id: index,
      type: round.type || 'game' // Default to 'game' for existing rounds without type
    }));
  });

  // Helper function to reorder items
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // Handle drag end for reordering rounds
  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Reorder the configs array
    const reorderedConfigs = reorder(configs, sourceIndex, destinationIndex);
    
    // Update the id property of each config to match new index
    const updatedConfigs = reorderedConfigs.map((config, index) => ({
      ...config,
      id: index,
    }));

    setConfigs(updatedConfigs);

    // Adjust the current page if necessary
    if (page === sourceIndex) {
      setPage(destinationIndex);
    } else if (page > sourceIndex && page <= destinationIndex) {
      setPage(page - 1);
    } else if (page < sourceIndex && page >= destinationIndex) {
      setPage(page + 1);
    }
  };

  // Handle duplicate round
  const handleDuplicateRound = (roundIndex) => {
    const configToDuplicate = { ...configs[roundIndex] };
    const newConfigs = [...configs];
    
    // Insert the duplicated config after the current round
    newConfigs.splice(roundIndex + 1, 0, {
      ...configToDuplicate,
      id: roundIndex + 1,
      // Preserve the type field for backward compatibility
      type: configToDuplicate.type || 'game',
    });
    
    // Update IDs for subsequent rounds
    const updatedConfigs = newConfigs.map((config, index) => ({
      ...config,
      id: index,
    }));
    
    setConfigs(updatedConfigs);
    
    // Set the page to the newly duplicated round
    setPage(roundIndex + 1);
  };

  // Handle delete round
  const handleDeleteRound = (roundIndex) => {
    if (configs.length <= 1) {
      // Don't allow deleting the last round
      return;
    }
    
    const newConfigs = configs.filter((_, index) => index !== roundIndex);
    
    // Update IDs for remaining rounds
    const updatedConfigs = newConfigs.map((config, index) => ({
      ...config,
      id: index,
    }));
    
    setConfigs(updatedConfigs);
    
    // Adjust current page if necessary
    if (page === roundIndex) {
      // If we're deleting the current page, go to the previous one or first round
      setPage(Math.max(0, roundIndex - 1));
    } else if (page > roundIndex) {
      // If current page is after the deleted round, shift it back
      setPage(page - 1);
    }
  };

  // Handle adding different types of rounds
  const handleAddRound = (roundType) => {
    const newRoundConfig = {
      id: configs.length,
      type: roundType,
      matchValue: DEFAULT_MATCH_VALUE,
      models: DEFAULT_CATEGORIES,
      // mode: DEFAULT_MODE,
      scheme: DEFAULT_SCHEME,
      shuffle: DEFAULT_SHUFFLE,
    };

    // Add type-specific configurations
    if (roundType === 'question') {
      newRoundConfig.questions = [];
    } else if (roundType === 'info') {
      newRoundConfig.content = '';
      newRoundConfig.title = '';
    } else if (roundType === 'pre-game-questions') {
      newRoundConfig.questions = questions.pre || [];
    } else if (roundType === 'post-game-questions') {
      newRoundConfig.questions = questions.post || [];
    }

    const newConfigs = [...configs, newRoundConfig];
    setConfigs(newConfigs);
    
    // Navigate to the newly added round
    setPage(configs.length);
  };

  useEffect(() => {
    function handleChange() {
      onChange({
        id: game?.id,
        name: name,
        configs: configs,
        questions: questions,
        surveyUrl: surveyUrl,
      });
    }

    handleChange();
  }, [game, name, page, configs, questions, surveyUrl, onChange]);

  // Handle migration of pre/post questions to configs
  useEffect(() => {
    if (!hasMigrated && game?.questions) {
      const existingQuestions = game.questions;
      const hasPreGameQuestions = existingQuestions.pre && existingQuestions.pre.length > 0;
      const hasPostGameQuestions = existingQuestions.post && existingQuestions.post.length > 0;
      const hasPreGameRound = configs.some(config => config.type === 'pre-game-questions');
      const hasPostGameRound = configs.some(config => config.type === 'post-game-questions');
      
      // If we have pre/post questions that haven't been migrated yet
      if ((hasPreGameQuestions && !hasPreGameRound) || (hasPostGameQuestions && !hasPostGameRound)) {
        let newConfigs = [...configs];
        
        // Add pre-game questions if they exist but aren't already in configs
        if (hasPreGameQuestions && !hasPreGameRound) {
          newConfigs.unshift({
            id: 0,
            type: 'pre-game-questions',
            questions: existingQuestions.pre,
          });
        }
        
        // Add post-game questions if they exist but aren't already in configs
        if (hasPostGameQuestions && !hasPostGameRound) {
          newConfigs.push({
            id: newConfigs.length,
            type: 'post-game-questions',
            questions: existingQuestions.post,
          });
        }
        
        // Update IDs to be sequential
        newConfigs = newConfigs.map((config, index) => ({
          ...config,
          id: index,
        }));
        
        setConfigs(newConfigs);
        
        // Remove pre/post questions from the questions state since they're now in configs
        const updatedQuestions = { ...questions };
        if (hasPreGameQuestions && !hasPreGameRound) {
          delete updatedQuestions.pre;
        }
        if (hasPostGameQuestions && !hasPostGameRound) {
          delete updatedQuestions.post;
        }
        setQuestions(updatedQuestions);
      }
      
      setHasMigrated(true);
    }
  }, [hasMigrated, game, configs, questions]);

  const [pageSteps, configView] = React.useMemo(() => {
    function configForIndex(index) {
      var tmpConfig = configs[index];
      
      // If config exists, return it as-is (don't modify existing configs)
      if (tmpConfig) {
        return tmpConfig;
      }
      
      // Only create and prepopulate for missing configs
      var newConfig = {
        matchValue: null,
        scheme: null,
        models: null,
        mode: null,
        shuffle: null,
        type: null, // Default type for new configs
      };
      return prepopulateConfigAtIndex(newConfig, index);
    }

    // iterates thru the models in reverse order, to get the last models selected
    function prepopulateConfigAtIndex(config, index) {
      // for (var i = index; i > 0; i--) {
      //   var prevConfig = Object.assign({}, configs[i - 1]);
      //   // attempt to assign the previous config's matchValue to the current
      //   if (!config.matchValue && !!prevConfig.matchValue) {
      //     config.matchValue = prevConfig.matchValue ?? 1000;
      //   }
      //   // attempt to assign the previous configs models to the current
      //   if (!config.models && !!prevConfig.models) {
      //     config.models = prevConfig.models;
      //   }

      //   // attempt to assign the previous configs scheme to the current
      //   if (!config.scheme && !!prevConfig.scheme) {
      //     config.scheme = prevConfig.scheme ?? DEFAULT_SCHEME;
      //   }

      //   // attempt to assign the previous configs mode to the current
      //   // if (!config.mode && !!prevConfig.mode) {
      //   //   config.mode = prevConfig.mode;
      //   // }

      //   // attempt to assign the previous configs shuffle to the current
      //   if (config.shuffle === null && !!prevConfig.shuffle) {
      //     config.shuffle = prevConfig.shuffle ?? DEFAULT_SHUFFLE;
      //   }

      //   if (!config.id) {
      //     config.id = index;
      //   }

      //   // Ensure type field exists for backward compatibility
      //   // if (!config.type) {
      //   //   config.type = 'game';
      //   // }
      // }
      return config;
    }

    function handleConfigChanged(e) {
      function sanitizeModels(models, mode) {
        return models?.map((model) => {
          let multiplier;
          if (mode === "donut") {
            multiplier = {
              low: parseFloat(model.multiplier?.low) || 1,
              high: parseFloat(model.multiplier?.high) || 1,
            };
          } else {
            multiplier = parseFloat(model.multiplier) || 1;
          }
          return { ...model, multiplier };
        });
      }

      var results = [...configs];
      var ele = results[page];
      ele = { ...ele, ...e };

      const newMode = e.mode || ele.mode;

      results[page] = {
        ...ele,
        models: sanitizeModels(ele?.models, newMode),
      };

      setConfigs(results);
    }

    const roundSteps = [];
    const view = {};

    function createPage({ id, name, index, isDraggable = false }) {
      if (isDraggable) {
        return (
          <RoundTab
            key={id}
            id={id}
            index={index}
            name={name}
            isActive={page === id}
            isDraggable={isDraggable}
            onPageChange={setPage}
            onDuplicate={handleDuplicateRound}
            onDelete={handleDeleteRound}
            canDelete={configs.length > 1}
            type={configs[index]?.mode ?? configs[index]?.type}
          />
        );
      }

      return (
        <Pagination.Item key={id} active={page === id} onClick={() => setPage(id)}>
          <Stack alignItems="center">
            {name ?? `Round ${id + 1}`}
            <small>{}</small>
          </Stack>
        </Pagination.Item>
      );
    }

    // Add all rounds (including pre/post game questions) as draggable
    for (let i = 0; i < configs.length; i++) {
      const roundId = i;
      const roundConfig = configForIndex(roundId);
      
      // Generate display name based on round type
      let displayName = roundConfig.prompt;
      if (!displayName) {
        if (roundConfig.type === 'pre-game-questions') {
          displayName = `Pre-Game Questions`;
        } else if (roundConfig.type === 'post-game-questions') {
          displayName = `Post-Game Questions`;
        } else if (roundConfig.type === 'question') {
          displayName = `Question Round ${roundId + 1}`;
        } else if (roundConfig.type === 'info') {
          displayName = `Info Round ${roundId + 1}`;
        } else {
          displayName = `Game Round ${roundId + 1}`;
        }
      }
      
      roundSteps.push(createPage({ id: roundId, index: i, isDraggable: true, name: displayName }));
      
      // Render appropriate form based on round type
      if (roundConfig.type === 'pre-game-questions' || roundConfig.type === 'post-game-questions') {
        view[roundId] = (
          <AddQuestionForm
            questions={roundConfig.questions || []}
            setQuestions={(value) => {
              const newConfigs = [...configs];
              newConfigs[roundId].questions = value;
              setConfigs(newConfigs);
            }}
          />
        );
      } else if (roundConfig.type === 'question') {
        view[roundId] = (
          <AddQuestionForm
            questions={roundConfig.questions || []}
            setQuestions={(value) => {
              const newConfigs = [...configs];
              newConfigs[roundId].questions = value;
              setConfigs(newConfigs);
            }}
          />
        );
      } else if (roundConfig.type === 'info') {
        view[roundId] = (
          <InfoRoundForm
            config={roundConfig}
            onChange={(updates) => {
              const newConfigs = [...configs];
              newConfigs[roundId] = { ...newConfigs[roundId], ...updates };
              setConfigs(newConfigs);
            }}
          />
        );
      } else {
        view[roundId] = (
          <RoundForm config={roundConfig ?? {}} onChange={handleConfigChanged} />
        );
      }
    }

    return [{ roundSteps }, view];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, page, questions]);

  // Add state for toast
  const [showToast, setShowToast] = useState(false);

  // Add a function to get the correct game URL
  const getGameUrl = () => {

    let baseURL = process.env.REACT_APP_BASE_URL;
    if (baseURL && !baseURL?.startsWith("http"))  baseURL = "http://" + baseURL;
    if (!baseURL) baseURL = "https://esgmachine.com";
  
    return `${baseURL}/${GameService.nameToRoute(name)}`;
  };

  const draftWarningText = () => {
    if (!game?.id || game?.status !== "active") {
      return <><p style={{ color: "red" }}>⚠️ save game before sharing link</p></>
    }
    return "";
  };

  // Final render of the Form Component
  return (
    <>
      <Form>
        <Form.Group className="mb-3" controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            isInvalid={!name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            placeholder="Name of the game"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="route">
          <Form.Label>
            Route{" "}
              {draftWarningText()}
          </Form.Label>
          <div className="input-group">
            <Form.Control
              value={getGameUrl()}
              disabled
              type="text"
              placeholder="Game route"
            />
            <ButtonGroup>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {draftWarningText()}{game?.id ? "Copy game URL to clipboard" : "Save game to get shareable URL"}
                  </Tooltip>
                }
              >
                <span> {/* Wrap in span because disabled buttons can't trigger overlays */}
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      const url = getGameUrl();
                      if (url) {
                        navigator.clipboard.writeText(url);
                        setShowToast(true);
                      }
                    }}
                    disabled={!game?.id}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </span>
              </OverlayTrigger>
              
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {game?.id ? "Open game in new tab" : "Save game to preview"}
                  </Tooltip>
                }
              >
                <span> {/* Wrap in span because disabled buttons can't trigger overlays */}
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      const url = getGameUrl();
                      if (url) {
                        window.open(url, '_blank');
                      }
                    }}
                    disabled={!game?.id}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </Button>
                </span>
              </OverlayTrigger>
            </ButtonGroup>
          </div>

          {/* Add Toast container */}
          <ToastContainer
            position="top-end"
            className="p-3"
            style={{ position: 'fixed', zIndex: 1000 }}
          >
            <Toast 
              onClose={() => setShowToast(false)} 
              show={showToast} 
              delay={3000} 
              autohide
              bg="success"
            >
              <Toast.Body className="text-white">
                <FontAwesomeIcon icon={faCopy} className="me-2" />
                URL copied to clipboard!
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </Form.Group>

        <Form.Group className="mb-3" controlId="surveyUrl">
          <Form.Label>
            Survey URL{" "}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-survey-url">
                  Game-specific survey URL. If not set, global analytics URL will be used. <br />
                  The <code>{`{sessionId}`}</code> placeholder will be replaced with the session ID.
                  <br />
                  Disable survey by setting to <code>N/A</code>.
                </Tooltip>
              }
            >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon="fa-info-circle" />
              </span>
            </OverlayTrigger>
            {draftWarningText()}
          </Form.Label>
          <Form.Control
            value={surveyUrl}
            placeholder={remoteConfig?.getValue("analyticsUrl")?.asString() ?? "N/A"}
            onChange={(e) => setSurveyUrl(e.target.value)}
            type="text"
          />
        </Form.Group>
        {/* {RoundsSection()} */}
        <Stack alignItems="center">
          <small>Drag to reorder rounds</small>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Pagination className={"justify-content-center"}>
              <Droppable droppableId="rounds" direction="horizontal">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ display: 'inline-flex' }}
                  >
                    {pageSteps.roundSteps}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              <AddRoundDropdown onAddRound={handleAddRound} />
            </Pagination>
          </DragDropContext>
        </Stack>
        {configView[page]}
      </Form>
    </>
  );
}

export default NewGame;
